/* eslint-disable @typescript-eslint/dot-notation */
import { Injectable } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { UtilityService } from 'src/app/core/services/utility.service';
import { ToggleHide, ToggleShow } from 'src/app/shared/ui/spinner/redux/spinner.actions';
import { DrbService } from '../services/drb.service';
import { DrbStateModel } from './drb-state.model';
import {
  BuildDrb,
  BuildDrbFail,
  BuildDrbSuccess,
  LoadDiff,
  LoadDiffFail,
  LoadDiffSuccess,
  LoadDrb,
  loadDrbAssociatedItems,
  loadDrbAssociatedItemsFail,
  loadDrbAssociatedItemsSuccess,
  LoadDrbByVersion,
  LoadDrbByVersionFail,
  LoadDrbByVersionSuccess,
  LoadDrbSuccess,
  LoadDrbVersions,
  LoadDrbVersionsSuccess,
  NoDrbDiff,
  NoDrbDraft,
  PostDrb,
  ReloadDrb,
  ReloadDrbSuccess,
  SaveComplete,
  SaveDrbState,
  SaveFail,
  SaveSourceFilesState,
  SaveSourceImagesState,
  SaveSourceLinksState,
  SaveSourceVideosState,
  SaveXliffState
} from './drb.actions';

@State<DrbStateModel>({
  name: 'drb',
  defaults: {
    marketing: {
      version: undefined,
      created_ts: undefined,
      created_by: undefined,
      last_updated_ts: undefined,
      last_updated_by: undefined,
      status: undefined,
      owner: undefined,
      contributors: undefined,
      bundle_version: undefined,
      xliff: undefined,
      source: undefined,
      draft: undefined,
      deployed_to_stage: undefined,
      deployed_to_stage_ts: undefined,
      deployed_to_prod: undefined,
      deployed_to_prod_ts: undefined,
      translate: undefined,
      translation_status: undefined,
      translations_submitted: undefined,
      translations_submitted_ts: undefined,
      translations_received: undefined,
      translations_received_ts: undefined
    },
    marketing_translations: {
      version: undefined,
      created_ts: undefined,
      created_by: undefined,
      last_updated_ts: undefined,
      last_updated_by: undefined,
      status: undefined,
      owner: undefined,
      contributors: undefined,
      bundle_version: undefined,
      xliff: undefined,
      source: undefined,
      draft: undefined,
      deployed_to_stage: undefined,
      deployed_to_stage_ts: undefined,
      deployed_to_prod: undefined,
      deployed_to_prod_ts: undefined,
      translate: undefined,
      translation_status: undefined,
      translations_submitted: undefined,
      translations_submitted_ts: undefined,
      translations_received: undefined,
      translations_received_ts: undefined
    },
    versions: {
      marketing: [],
      marketing_translations: []
    },
    associatedItems: [],
    ids: [],
    diff: [],
    deploying: false,
    translating: false,
    error: null,
    noDrbDraft: null
  }
})
@Injectable()
export class DrbState {
  constructor(
    private service: DrbService,
    private toastrService: NbToastrService,
    private utilityService: UtilityService
  ) {}

  @Selector()
  public static marketing(state: DrbStateModel) {
    return state.marketing;
  }

  @Selector()
  public static marketing_translations(state: DrbStateModel) {
    return state.marketing_translations;
  }

  @Selector()
  public static versions(state: DrbStateModel) {
    return state.versions;
  }

  @Selector()
  public static ids(state: DrbStateModel) {
    return state.ids;
  }

  @Selector()
  public static diff(state: DrbStateModel) {
    return state.diff;
  }

  @Selector()
  public static deploying(state: DrbStateModel) {
    return state.deploying;
  }

  @Selector()
  public static translating(state: DrbStateModel) {
    return state.translating;
  }

  @Selector()
  public static noDrbDraft(state: DrbStateModel) {
    return state.noDrbDraft;
  }

  @Selector()
  public static associatedItems(state: DrbStateModel) {
    return state.associatedItems;
  }

  public static getByKey(key: string) {
    return createSelector([DrbState], (state: DrbStateModel) => state[key]);
  }

  @Action(LoadDrb)
  public async loadDrb(ctx: StateContext<DrbStateModel>, { bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    this.service.loadDrb(bundle_name);
  }

  @Action(LoadDrbByVersion)
  public async loadDrbByVersion(ctx: StateContext<DrbStateModel>, { version, bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    this.service.loadDrbByVersion(version, bundle_name);
  }

  @Action(LoadDrbVersions)
  public async loadDrbVersions(ctx: StateContext<DrbStateModel>, { bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    this.service.loadDrbVersions(bundle_name);
  }

  @Action(LoadDiff)
  public async loadDiff(ctx: StateContext<DrbStateModel>, { bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    this.service.loadDiff(bundle_name);
  }

  @Action(LoadDrbSuccess)
  public loadDrbSuccess(ctx: StateContext<DrbStateModel>, { payload, bundle_name }: any) {
    const state = ctx.getState();
    const deployInProgress =
      payload['status'] === 'DEPLOY_STARTED_PROD' ||
      payload['status'] === 'DEPLOY_STARTED_STAGE' ||
      payload['status'] === 'BUILD_STARTED' ||
      payload['status'] === 'BUILD_SUCCEEDED'
        ? true
        : false;
    const translationInProgress =
      payload['translations_submitted'] === true && payload['translations_received'] === false;

    let new_state = {
      ...state,
      ids: [
        ...payload['xliff']['en'].map((e) => e.id),
        ...payload['source']['files'].map((e) => e.id),
        ...payload['source']['images'].map((e) => e.id),
        ...payload['source']['links'].map((e) => e.id),
        ...payload['source']['videos'].map((e) => e.id)
      ],
      deploying: deployInProgress,
      translating: translationInProgress,
      noDrbDraft: false
    };
    new_state[bundle_name] = { ...payload };
    ctx.setState(new_state);
    ctx.dispatch(ToggleHide);
  }

  @Action(LoadDrbByVersionSuccess)
  public loadDrbByVersionSuccess(ctx: StateContext<DrbStateModel>, { payload, bundle_name }: any) {
    const state = ctx.getState();
    const deployInProgress =
      payload['status'] === 'DEPLOY_STARTED_PROD' ||
      payload['status'] === 'DEPLOY_STARTED_STAGE' ||
      payload['status'] === 'BUILD_STARTED' ||
      payload['status'] === 'BUILD_SUCCEEDED'
        ? true
        : false;
    const translationInProgress =
      payload['translations_submitted'] === true && payload['translations_received'] === false;
    let new_state = { ...state, deploying: deployInProgress, translating: translationInProgress };
    new_state[bundle_name] = payload;
    ctx.setState(new_state);
    ctx.dispatch(ToggleHide);
  }

  @Action(LoadDrbByVersionFail)
  public loadDrbByVersionFail(ctx: StateContext<DrbStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.dispatch(ToggleHide);
  }

  @Action(LoadDrbVersionsSuccess)
  public loadDrbVersionSuccess(ctx: StateContext<DrbStateModel>, { payload, bundle_name }: any) {
    const state = ctx.getState();
    const deployInProgress =
      payload['draft_status'] === 'BUILD_STARTED' ||
      payload['draft_status'] === 'DEPLOY_STARTED_PROD' ||
      payload['draft_status'] === 'DEPLOY_STARTED_STAGE' ||
      payload['draft_status'] === 'BUILD_SUCCEEDED'
        ? true
        : false;
    const translationInProgress =
      'translations_submitted' in payload &&
      'translations_received' in payload &&
      payload['translations_submitted'] === true &&
      payload['translations_received'] === false
        ? true
        : false;
    let versions = { ...state.versions };
    versions[bundle_name] = payload;
    let new_state = { ...state, versions, deploying: deployInProgress, translating: translationInProgress };
    ctx.setState(new_state);
    ctx.dispatch(ToggleHide);
  }

  @Action(LoadDiffSuccess)
  public loadDiffSuccess(ctx: StateContext<DrbStateModel>, { payload }: any) {
    const state = ctx.getState();
    const deployInProgress =
      payload['draft_status'] === 'BUILD_STARTED' ||
      payload['draft_status'] === 'DEPLOY_STARTED_PROD' ||
      payload['draft_status'] === 'DEPLOY_STARTED_STAGE' ||
      payload['draft_status'] === 'BUILD_SUCCEEDED'
        ? true
        : false;
    const translationInProgress =
      'translations_submitted' in payload &&
      'translations_received' in payload &&
      payload['translations_submitted'] === true &&
      payload['translations_received'] === false
        ? true
        : false;

    ctx.setState({
      ...state,
      diff: payload,
      deploying: deployInProgress,
      translating: translationInProgress
    });
    ctx.dispatch(ToggleHide);
  }

  @Action(LoadDiffFail)
  public loadDiffFail(ctx: StateContext<DrbStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.dispatch(ToggleHide);
  }

  @Action(loadDrbAssociatedItems)
  public async loadDrbAssociatedItems(ctx: StateContext<DrbStateModel>, { drb_id }: any) {
    ctx.dispatch(ToggleShow);
    this.service.loadDrbAssociatedItems(drb_id);
  }

  @Action(loadDrbAssociatedItemsSuccess)
  public async loadDrbAssociatedItemsSuccess(ctx: StateContext<DrbStateModel>, payload: any) {
    const state = ctx.getState();

    ctx.setState({
      ...state,
      associatedItems: payload
    });
    ctx.dispatch(ToggleHide);
  }

  @Action(loadDrbAssociatedItemsFail)
  public loadDrbAssociatedItemsFail(ctx: StateContext<DrbStateModel>) {
    const state = ctx.getState();

    ctx.setState({
      ...state,
      associatedItems: []
    });
    ctx.dispatch(ToggleHide);
  }

  @Action(ReloadDrb)
  public async reloadDrb(ctx: StateContext<DrbStateModel>, { bundle_name }) {
    this.toastrService.show('', $localize`Fetch Initiated`, { status: 'info' });
    this.service.loadDrbAmplify(bundle_name, true);
  }

  @Action(ReloadDrbSuccess)
  public reloadDrbSuccess(ctx: StateContext<DrbStateModel>, { payload, bundle_name }: any) {
    const state = ctx.getState();
    let new_state = {
      ...state
    };
    new_state[bundle_name] = payload;
    ctx.setState(new_state);
  }

  @Action(PostDrb)
  public async postDrb(ctx: StateContext<DrbStateModel>, { payload, bundle_name }: any) {
    this.service.postDrbAmplify(payload, bundle_name);
  }

  @Action(SaveComplete)
  public drbComplete(ctx: StateContext<DrbStateModel>, { data, bundle_name }: any) {
    const state = ctx.getState();
    let new_state = {
      ...state,
      noDrbDraft: false
    };
    new_state[bundle_name] = data;
    ctx.setState(new_state);
    this.toastrService.show('', $localize`Save: Complete`, { status: 'success' });
    ctx.dispatch(ToggleHide);
  }

  @Action(SaveXliffState)
  public saveXliff(ctx: StateContext<DrbStateModel>, { data, bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    const draftDrb = this.getDrbObject(ctx, bundle_name);
    draftDrb.xliff = { en: data };
    draftDrb.last_updated_by = data[0]['last_updated_by'];
    draftDrb.last_updated_ts = data[0]['last_updated_ts'];
    draftDrb.bundle_name = bundle_name;

    this.putDrb(draftDrb, bundle_name);
    this.updateIds(ctx, data);
  }

  @Action(SaveSourceFilesState)
  public saveSourceFiles(ctx: StateContext<DrbStateModel>, { data, bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    const draftDrb = this.getDrbObject(ctx, bundle_name);
    draftDrb.source.files = data;
    if (data.length > 0) {
      draftDrb.last_updated_by = data[0]['last_updated_by'];
      draftDrb.last_updated_ts = data[0]['last_updated_ts'];
    }
    draftDrb.bundle_name = bundle_name;

    this.putDrb(draftDrb, bundle_name);
    this.updateIds(ctx, data);
  }

  @Action(SaveSourceImagesState)
  public saveSourceImages(ctx: StateContext<DrbStateModel>, { data, bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    const draftDrb = this.getDrbObject(ctx, bundle_name);
    draftDrb.source.images = data;

    if (data.length > 0) {
      draftDrb.last_updated_by = data[0]['last_updated_by'];
      draftDrb.last_updated_ts = data[0]['last_updated_ts'];
    }
    draftDrb.bundle_name = bundle_name;

    this.putDrb(draftDrb, bundle_name);
    this.updateIds(ctx, data);
  }

  @Action(SaveSourceLinksState)
  public saveSourceLinks(ctx: StateContext<DrbStateModel>, { data, bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    const draftDrb = this.getDrbObject(ctx, bundle_name);
    draftDrb.source.links = data;

    if (data.length > 0) {
      draftDrb.last_updated_by = data[0]['last_updated_by'];
      draftDrb.last_updated_ts = data[0]['last_updated_ts'];
    }
    draftDrb.bundle_name = bundle_name;

    this.putDrb(draftDrb, bundle_name);
    this.updateIds(ctx, data);
  }

  @Action(SaveSourceVideosState)
  public saveSourceVideos(ctx: StateContext<DrbStateModel>, { data, bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    const draftDrb = this.getDrbObject(ctx, bundle_name);
    draftDrb.source.videos = data;

    if (data.length > 0) {
      draftDrb.last_updated_by = data[0]['last_updated_by'];
      draftDrb.last_updated_ts = data[0]['last_updated_ts'];
    }
    draftDrb.bundle_name = bundle_name;

    this.putDrb(draftDrb, bundle_name);
    this.updateIds(ctx, data);
  }

  @Action(SaveDrbState)
  public saveDrb(ctx: StateContext<DrbStateModel>, { data, bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    this.putDrb(data, bundle_name);
    this.updateIds(ctx, data);
  }

  @Action(SaveFail)
  public drbFail(ctx: StateContext<DrbStateModel>, { payload }: any) {
    const state = ctx.getState();
    const error = this.utilityService.convertErrorToMessage(payload);
    ctx.setState({
      ...state,
      error
    });
    ctx.dispatch(ToggleHide);
    this.toastrService.show('', $localize`Save: Failed: ${error}`, { status: 'danger' });
  }

  @Action(BuildDrb)
  public buildDrb(ctx: StateContext<DrbStateModel>, { bundle_name }: any) {
    ctx.dispatch(ToggleShow);
    const state = ctx.getState();
    this.service.buildDrb(bundle_name);

    //this.checkDeployStatus(ctx);

    if (
      state.diff['draft_status'] === 'DEPLOY_SUCCEEDED_PROD' ||
      state.diff['draft_status'] === 'DEPLOY_SUCCEEDED_STAGE'
    ) {
      ctx.dispatch(BuildDrbSuccess);
    }
    if (
      state.diff['draft_status'] === 'BUILD_FAILED' ||
      state.diff['draft_status'] === 'DEPLOY_FAILED_PROD' ||
      state.diff['draft_status'] === 'DEPLOY_FAILED_STAGE'
    ) {
      ctx.dispatch(BuildDrbFail);
    }
    // this.service.loadDiff();
    // this.service.loadDrb();
  }

  @Action(BuildDrbSuccess)
  public buildDrbSuccess(ctx: StateContext<DrbStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState(
      patch({
        deploying: false
      })
    );
    ctx.dispatch(ToggleHide);
  }
  @Action(BuildDrbFail)
  public buildDrbFail(ctx: StateContext<DrbStateModel>, { payload }: any) {
    const state = ctx.getState();
    ctx.setState(
      patch({
        deploying: false
      })
    );
    ctx.dispatch(ToggleHide);
  }

  @Action(NoDrbDraft)
  public noDrbDraft(ctx: StateContext<DrbStateModel>) {
    this.toastrService.show('', $localize`DRB draft version not found. Create new draft.`, { status: 'info' });
    ctx.setState(
      patch({
        noDrbDraft: true,
        deploying: false
      })
    );
    ctx.dispatch(ToggleHide);
  }

  @Action(NoDrbDiff)
  public NoDrbDiff(ctx: StateContext<DrbStateModel>) {
    this.toastrService.show('', $localize`DRB draft version not found for comparison.`, { status: 'info' });
    ctx.setState(
      patch({
        diff: [],
        deploying: false
      })
    );
    ctx.dispatch(ToggleHide);
  }

  public async putDrb(payload: any, bundle_name: string) {
    this.service.putDrbAmplify(payload, bundle_name);
  }

  public getDrbObject(ctx: StateContext<DrbStateModel>, bundle_name: string) {
    const state = ctx.getState();
    const draftDrb = {
      version: state[bundle_name].version,
      bundle_name: bundle_name,
      created_by: state[bundle_name].created_by,
      created_ts: state[bundle_name].created_ts,
      status: state[bundle_name].status,
      last_updated_by: state[bundle_name].last_updated_by,
      last_updated_ts: state[bundle_name].last_updated_ts,
      owner: state[bundle_name].owner,
      contributors: state[bundle_name].contributors.filter((n) => n),
      xliff: state[bundle_name].xliff,
      source: {
        files: state[bundle_name].source.files,
        images: state[bundle_name].source.images,
        links: state[bundle_name].source.links,
        videos: state[bundle_name].source.videos
      },
      draft: state[bundle_name].draft,
      deployed_to_stage: state[bundle_name].deployed_to_stage,
      deployed_to_stage_ts: state[bundle_name].deployed_to_stage_ts,
      deployed_to_prod: state[bundle_name].deployed_to_prod,
      deployed_to_prod_ts: state[bundle_name].deployed_to_prod_ts,

      translate: state[bundle_name].translate,
      translation_status: state[bundle_name].translation_status,
      translations_submitted: state[bundle_name].translations_submitted,
      translations_submitted_ts: state[bundle_name].translations_submitted_ts,
      translations_received: state[bundle_name].translations_received,
      translations_received_ts: state[bundle_name].translations_received_ts
    };

    return draftDrb;
  }

  public updateIds(ctx: StateContext<DrbStateModel>, data: any) {
    const state = ctx.getState();
    const newIds = JSON.parse(JSON.stringify(state.ids));
    if (data.length > 0) {
      newIds.push(data[data.length - 1].id);
      ctx.setState(
        patch({
          ids: newIds
        })
      );
    }
  }

  public checkDeployStatus(ctx: StateContext<DrbStateModel>, { bundle_name }: any) {
    const state = ctx.getState();

    this.service.loadDiff(bundle_name);
    while (
      state.diff['draft_status'] === 'BUILD_STARTED' ||
      state.diff['draft_status'] === 'DEPLOY_STARTED_PROD' ||
      state.diff['draft_status'] === 'DEPLOY_STARTED_STAGE'
    ) {
      ctx.setState(
        patch({
          deploying: true
        })
      );
      this.service.loadDiff(bundle_name);
      this.sleep(5);
    }
  }

  public sleep(sec) {
    return new Promise((resolve) => setTimeout(resolve, sec * 1000));
  }
}
